import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Settings, Youtube, Save, Mail, RotateCcw, Eye, ChevronDown, ChevronRight, Cloud, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage({ user, logout }) {
  const [settings, setSettings] = useState({ youtube_link: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, { withCredentials: true });
        setSettings(response.data || { youtube_link: "" });
      } catch (e) {
        console.error("Error fetching settings:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success("Impostazioni salvate");
    } catch (e) {
      console.error("Error saving settings:", e);
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout user={user} logout={logout} testId="settings-page" className="overflow-auto">
        <div className="p-8 max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Impostazioni</h1>
            <p className="text-slate-600 mt-1">Configura le impostazioni del portale e i modelli email</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Youtube className="w-5 h-5 text-rose-500" />
                    Video Presentazione
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label>Link Video (YouTube, Tella, Loom, Vimeo)</Label>
                    <Input
                      value={settings.youtube_link || ""}
                      onChange={(e) => setSettings({ ...settings, youtube_link: e.target.value })}
                      placeholder="https://www.tella.tv/video/... oppure YouTube/Loom/Vimeo"
                      data-testid="youtube-link-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Questo video sarà visibile nella pagina offerta del cliente. Supporta YouTube, Tella, Loom e Vimeo.</p>
                  </div>
                </CardContent>
              </Card>

              {/* Allarme Memo — suoneria e email destinataria */}
              <Card data-testid="memo-alarm-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Settings className="w-5 h-5 text-amber-500" />
                    Allarme Memo Follow UP
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Suoneria allarme sonoro</Label>
                    <div className="flex gap-2 mt-2">
                      {[
                        { key: "soft", label: "Soft", desc: "Delicato — 1.5s a 523Hz" },
                        { key: "normal", label: "Normale", desc: "Standard — 3s a 880Hz" },
                        { key: "urgent", label: "Urgente", desc: "Intenso — 5s a 1200Hz" },
                      ].map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setSettings({ ...settings, memo_alarm_tone: t.key })}
                          data-testid={`tone-${t.key}`}
                          className={`bg-transparent flex-1 text-left p-3 rounded-lg border-2 transition-colors ${
                            (settings.memo_alarm_tone || "normal") === t.key
                              ? "border-amber-500"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          style={{
                            backgroundColor: (settings.memo_alarm_tone || "normal") === t.key ? "#fffbeb" : "#ffffff",
                            color: "#0f172a",
                          }}
                        >
                          <div className="font-semibold text-sm" style={{ color: "#0f172a" }}>{t.label}</div>
                          <div className="text-xs mt-0.5" style={{ color: "#64748b" }}>{t.desc}</div>
                        </button>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      data-testid="test-tone-btn"
                      onClick={() => {
                        try {
                          const AudioCtx = window.AudioContext || window.webkitAudioContext;
                          if (!AudioCtx) { toast.error("Audio non supportato"); return; }
                          const ctx = new AudioCtx();
                          const osc = ctx.createOscillator();
                          const gain = ctx.createGain();
                          const cfg = {
                            soft:   { freq: 523, duration: 1.5, maxGain: 0.15, pulseInterval: 0.5 },
                            normal: { freq: 880, duration: 3.0, maxGain: 0.30, pulseInterval: 0.6 },
                            urgent: { freq: 1200, duration: 5.0, maxGain: 0.45, pulseInterval: 0.3 },
                          }[settings.memo_alarm_tone || "normal"];
                          osc.type = "sine";
                          osc.frequency.value = cfg.freq;
                          gain.gain.setValueAtTime(0.001, ctx.currentTime);
                          gain.gain.exponentialRampToValueAtTime(cfg.maxGain, ctx.currentTime + 0.05);
                          osc.connect(gain);
                          gain.connect(ctx.destination);
                          osc.start();
                          const start = ctx.currentTime;
                          for (let t = cfg.pulseInterval; t < cfg.duration; t += 2 * cfg.pulseInterval) {
                            gain.gain.setValueAtTime(0.001, start + t);
                            gain.gain.setValueAtTime(cfg.maxGain, start + t + cfg.pulseInterval);
                          }
                          setTimeout(() => { try { osc.stop(); ctx.close(); } catch {/*noop*/} }, (cfg.duration + 0.2) * 1000);
                          toast.success("Riproducendo tono di prova...");
                        } catch (e) {
                          toast.error("Errore riproduzione: " + e.message);
                        }
                      }}
                    >
                      ▶ Testa tono
                    </Button>
                  </div>
                  <div>
                    <Label>Email destinataria notifica (commerciale)</Label>
                    <Input
                      type="email"
                      value={settings.memo_alarm_email || "commerciale@showroomingenio.it"}
                      onChange={(e) => setSettings({ ...settings, memo_alarm_email: e.target.value })}
                      placeholder="commerciale@showroomingenio.it"
                      data-testid="memo-alarm-email-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Se un memo Follow UP resta scaduto per più di 1 ora senza essere silenziato, un'email di allerta viene inviata a questo indirizzo.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Settings className="w-5 h-5 text-slate-600" />
                    Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-600">Nome</span>
                      <span className="font-medium text-slate-900">{user?.name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-600">Email</span>
                      <span className="font-medium text-slate-900">{user?.email}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Salvataggio..." : "Salva Impostazioni"}
              </Button>

              {/* Backup Notion — solo admin */}
              {isAdmin && <NotionBackupSection />}

              {/* Modelli Email — solo admin */}
              {isAdmin && <MailTemplatesSection />}
            </div>
          )}
        </div>
    </PageLayout>
  );
}

/* ===========================================================
   NotionBackupSection — Backup Clienti su Notion
   =========================================================== */
function NotionBackupSection() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [runningFatture, setRunningFatture] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notion-backup/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    toast.info("Backup Clienti in corso...");
    try {
      const res = await axios.post(`${API}/notion-backup/run`, {}, { withCredentials: true });
      const s = res.data.stats;
      toast.success(`Clienti: ${s.created} nuovi, ${s.updated} aggiornati${s.errors ? `, ${s.errors} errori` : ""}`);
      await fetchStatus();
    } catch (e) {
      console.error(e);
      toast.error(`Errore backup: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const handleRunFatture = async () => {
    if (runningFatture) return;
    setRunningFatture(true);
    toast.info("Backup Fatture in corso...");
    try {
      const res = await axios.post(`${API}/notion-backup/fatture/run`, {}, { withCredentials: true });
      const s = res.data.stats;
      toast.success(`Fatture: ${s.created} nuove, ${s.updated} aggiornate${s.errors ? `, ${s.errors} errori` : ""}`);
      await fetchStatus();
    } catch (e) {
      console.error(e);
      toast.error(`Errore backup fatture: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunningFatture(false);
    }
  };

  const lastRun = status?.last_run;
  const lastRunDate = lastRun?.finished_at ? new Date(lastRun.finished_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : null;

  return (
    <Card className="mt-6" data-testid="notion-backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Cloud className="w-5 h-5 text-slate-700" />
          Backup Clienti su Notion
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Sincronizza su Notion i dati del CRM per avere sempre un archivio consultabile: <strong>Clienti</strong> (dati intervista, articoli, pagamenti totali) nella pagina "Backup CRM INGENIO" e <strong>Fatture</strong> (per tranche) nel database "SOS Crm Fatture". Sync automatica ogni giorno alle 07:00.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          {status?.configured ? (
            <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-slate-700">Integrazione Notion configurata</span></>
          ) : (
            <><AlertCircle className="w-4 h-4 text-red-600" /><span className="text-red-700">Notion API key non configurata</span></>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span className="font-medium">Sincronizzazione automatica:</span>
          <span>{status?.schedule || "—"}</span>
        </div>
        {lastRun && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
            <div className="font-medium text-slate-800 mb-1">Ultima esecuzione — Clienti</div>
            <div className="text-slate-600">
              {lastRunDate} — {lastRun.total} totali, <span className="text-green-700 font-semibold">{lastRun.created} nuovi</span>, <span className="text-blue-700 font-semibold">{lastRun.updated} aggiornati</span>
              {lastRun.errors > 0 && <>, <span className="text-red-700 font-semibold">{lastRun.errors} errori</span></>}
            </div>
          </div>
        )}
        {status?.last_run_fatture && (() => {
          const lrf = status.last_run_fatture;
          const lrfDate = lrf?.finished_at ? new Date(lrf.finished_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : null;
          return (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
              <div className="font-medium text-slate-800 mb-1">Ultima esecuzione — Fatture (SOS Crm Fatture)</div>
              <div className="text-slate-600">
                {lrfDate} — <span className="text-green-700 font-semibold">{lrf.created} nuove</span>, <span className="text-blue-700 font-semibold">{lrf.updated} aggiornate</span>, {lrf.skipped} vuote
                {lrf.errors > 0 && <>, <span className="text-red-700 font-semibold">{lrf.errors} errori</span></>}
              </div>
            </div>
          );
        })()}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleRun}
            disabled={running || !status?.configured}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="notion-backup-run-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Sincronizzazione..." : "Sincronizza Clienti"}
          </Button>
          <Button
            onClick={handleRunFatture}
            disabled={runningFatture || !status?.configured}
            variant="outline"
            className="border-slate-300"
            data-testid="notion-fatture-run-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${runningFatture ? "animate-spin" : ""}`} />
            {runningFatture ? "Sincronizzazione..." : "Sincronizza Fatture"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ===========================================================
   MailTemplatesSection — Editor dei modelli email
   =========================================================== */
function MailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mail-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Errore nel caricamento dei modelli email");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  return (
    <Card data-testid="mail-templates-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="w-5 h-5 text-blue-600" />
          Modelli Email
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Modifica i testi delle email inviate al cliente. Usa <code className="bg-slate-100 px-1 rounded">{`{{placeholder}}`}</code> per inserire dati dinamici (es. <code className="bg-slate-100 px-1 rounded">{`{{cliente_nome}}`}</code>).
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6"><div className="spinner inline-block" /></div>
        ) : (
          <div className="space-y-2">
            {templates.map((tpl) => (
              <TemplateAccordion
                key={tpl.key}
                template={tpl}
                expanded={expandedKey === tpl.key}
                onToggle={() => setExpandedKey(expandedKey === tpl.key ? null : tpl.key)}
                onUpdated={fetchTemplates}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplateAccordion({ template, expanded, onToggle, onUpdated }) {
  const [form, setForm] = useState({
    subject: template.subject || "",
    intro: template.intro || "",
    outro: template.outro || "",
    signature_name: template.signature_name || "",
    signature_role: template.signature_role || "",
  });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    setForm({
      subject: template.subject || "",
      intro: template.intro || "",
      outro: template.outro || "",
      signature_name: template.signature_name || "",
      signature_role: template.signature_role || "",
    });
  }, [template]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/mail-templates/${template.key}`, form, { withCredentials: true });
      toast.success("Modello salvato");
      onUpdated();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Ripristinare il modello "${template.name}" ai valori di default? Le tue modifiche andranno perse.`)) return;
    setSaving(true);
    try {
      await axios.post(`${API}/mail-templates/${template.key}/reset`, {}, { withCredentials: true });
      toast.success("Modello ripristinato");
      onUpdated();
    } catch (e) {
      toast.error("Errore nel ripristino");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/mail-templates/${template.key}/preview`, form, { withCredentials: true });
      setPreviewHtml(res.data.body_html);
      setPreviewSubject(res.data.subject);
    } catch (e) {
      toast.error("Errore nell'anteprima");
    } finally {
      setLoadingPreview(false);
    }
  };

  const categoryBadge = {
    posa: "bg-emerald-100 text-emerald-800",
    documenti: "bg-blue-100 text-blue-800",
    pagamenti: "bg-amber-100 text-amber-800",
  }[template.category] || "bg-slate-100 text-slate-800";

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`template-${template.key}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-transparent hover:bg-slate-50 transition-colors text-left"
        data-testid={`template-toggle-${template.key}`}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <div>
            <div className="font-semibold text-slate-900">{template.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${categoryBadge}`}>
                {template.category}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-slate-100 text-slate-600">
                {template.layout}
              </span>
              {template.updated_by && template.updated_by !== "system" && (
                <span className="text-xs text-slate-500">
                  · modificato da {template.updated_by}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="p-4 border-t bg-slate-50 space-y-4">
          {/* Placeholder reference */}
          {template.placeholders?.length > 0 && (
            <div className="bg-white p-3 rounded border">
              <div className="text-xs font-semibold text-slate-700 mb-2">📌 Placeholder disponibili (clicca per copiare)</div>
              <div className="flex flex-wrap gap-1.5">
                {template.placeholders.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`{{${ph}}}`);
                      toast.success(`Copiato: {{${ph}}}`);
                    }}
                    className="text-xs bg-slate-100 hover:bg-blue-100 hover:text-blue-800 px-2 py-1 rounded font-mono transition-colors"
                    data-testid={`placeholder-${template.key}-${ph}`}
                  >
                    {`{{${ph}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Oggetto */}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Oggetto</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="bg-white"
              data-testid={`template-subject-${template.key}`}
            />
          </div>

          {/* Intro */}
          <div>
            <Label className="text-xs font-semibold text-slate-700">
              {template.layout === "flat" ? "Corpo email (HTML)" : "Testo introduttivo (HTML)"}
            </Label>
            <Textarea
              value={form.intro}
              onChange={(e) => setForm({ ...form, intro: e.target.value })}
              className="bg-white font-mono text-xs min-h-[180px]"
              data-testid={`template-intro-${template.key}`}
            />
            {template.layout === "wrapped" && (
              <p className="text-[11px] text-slate-500 mt-1">
                Appare <b>prima</b> delle sezioni dinamiche (date, articoli, avvisi, ecc.)
              </p>
            )}
          </div>

          {/* Outro (solo wrapped) */}
          {template.layout === "wrapped" && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Testo conclusivo (HTML)</Label>
              <Textarea
                value={form.outro}
                onChange={(e) => setForm({ ...form, outro: e.target.value })}
                className="bg-white font-mono text-xs min-h-[120px]"
                data-testid={`template-outro-${template.key}`}
              />
              <p className="text-[11px] text-slate-500 mt-1">Appare <b>dopo</b> le sezioni dinamiche, prima della firma</p>
            </div>
          )}

          {/* Firma */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Firma - Nome</Label>
              <Input
                value={form.signature_name}
                onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
                className="bg-white"
                data-testid={`template-signature-name-${template.key}`}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Firma - Ruolo</Label>
              <Input
                value={form.signature_role}
                onChange={(e) => setForm({ ...form, signature_role: e.target.value })}
                className="bg-white"
                data-testid={`template-signature-role-${template.key}`}
              />
            </div>
          </div>

          {/* Preview */}
          {previewHtml && (
            <div className="bg-white p-3 rounded border">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-700">🔍 Anteprima (con dati di esempio)</div>
                <div className="text-xs text-slate-500 truncate ml-3">Oggetto: <span className="font-semibold">{previewSubject}</span></div>
              </div>
              <iframe
                title={`preview-${template.key}`}
                srcDoc={previewHtml}
                className="w-full h-[500px] bg-white rounded border-0"
                sandbox=""
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
              data-testid={`template-reset-${template.key}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Ripristina Default
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                disabled={loadingPreview}
                data-testid={`template-preview-${template.key}`}
              >
                <Eye className="w-3.5 h-3.5 mr-1" />
                {loadingPreview ? "..." : "Anteprima"}
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid={`template-save-${template.key}`}
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                {saving ? "Salvataggio..." : "Salva Modello"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
